import { Dispatch, UnknownAction } from '@reduxjs/toolkit'
import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react'

import { prepareDefaultHeaders } from '../../api'
import { BlueprintFamilyExampleImage } from '../../api/blueprintFamilies/exampleImages'
import { resetBlueprintFamilyDensityScalarCache } from '../../api/blueprintFamilies/trendsBatch'
import { coreApi } from '../../api/core'
import { TranslationMap } from '../../types/TranslationMap'
import { acceptedEventTypesBody, exampleImagesBody } from './blueprintFamilyWritePayload'

// An event type the blueprint family accepts. categoryId is the "event" tag group the type belongs
// to, and is absent for types without one. count is how many *active* events of that type the
// family has, so an accepted type nothing uses yet reports 0.
export interface BlueprintFamilyEventType {
  typeId: string
  categoryId?: string
  count: number
}

// An active event of the family's game blueprints whose type the family does not accept.
export interface BlueprintFamilyUnacceptedEvent {
  eventId: string
  name: string
  gameId: string
  // The game-level blueprint the event is linked to.
  blueprintId: string
  // The event's typeAccepted: an analyst marked it as a legitimate exception. It is listed either way.
  accepted: boolean
}

// An event type the family's active events use without the family accepting it, with those
// events. Never empty: a type is only reported here while an active event uses it.
export interface BlueprintFamilyUnacceptedEventType {
  typeId: string
  categoryId?: string
  events: BlueprintFamilyUnacceptedEvent[]
}

// Blueprint Families are global across all LiveOps tracked games; a game's own
// blueprint may belong to a family or not.
export interface BlueprintFamily {
  id: string
  names: TranslationMap
  descriptions: TranslationMap
  discontinued: boolean
  // Ordered list of example screenshots surfaced on the Data Glossary card gallery
  // and the Live Event Analytics family page. Each one is picked off a tracked live
  // event rather than uploaded, so it carries the event it came from. Only `url` and
  // `eventId` are stored; the rest is resolved by the BE and ignored on write.
  glossaryImages?: BlueprintFamilyExampleImage[]
  createdAt?: number
  modifiedAt?: number
  // The event types this family accepts. There is no allowedEventTypeIds on the read side; that
  // field exists on writes only, and these are what belong in it. Ordered by count descending and
  // then by typeId, so accepted types nothing uses yet come last. An empty list means nothing has
  // been declared for the family yet.
  eventTypes?: BlueprintFamilyEventType[]
  // The types its active events use that it does not accept, events included, in the same order.
  // Never send these back in allowedEventTypeIds unless the analyst chose to accept them.
  unacceptedEventTypes?: BlueprintFamilyUnacceptedEventType[]
}

// The write side takes at most this many accepted event type ids.
export const MAX_ACCEPTED_EVENT_TYPES = 10

export interface CreateBlueprintFamilyRequest {
  names: TranslationMap
  descriptions: TranslationMap
  discontinued: boolean
  glossaryImages?: BlueprintFamilyExampleImage[]
  // Write-only: the response reports the accepted types through eventTypes instead. At most
  // MAX_ACCEPTED_EVENT_TYPES ids.
  allowedEventTypeIds?: string[]
}

// The customer Live Event Analytics views read families through `coreApi`, a separate
// API instance, so an edit here has to invalidate its `BlueprintFamilies` tag (and the
// density scalar module cache, which sits outside RTK) explicitly.
export async function invalidateCustomerBlueprintFamilies(dispatch: Dispatch<UnknownAction>, queryFulfilled: Promise<unknown>): Promise<void> {
  try {
    await queryFulfilled
  } catch {
    return
  }
  resetBlueprintFamilyDensityScalarCache()
  dispatch(coreApi.util.invalidateTags(['BlueprintFamilies']))
}

export const internalMarketsApi = createApi({
  reducerPath: 'internalMarketsApi',
  baseQuery: fetchBaseQuery({
    baseUrl: window.GR_API_URLS.API_URL_CORE + '/i/',
    prepareHeaders: prepareDefaultHeaders,
  }),
  tagTypes: ['trackedBlueprints'],
  endpoints: (builder) => ({
    getTrackedBlueprints: builder.query<BlueprintFamily[], void>({
      query: () => 'blueprint-families',
      providesTags: ['trackedBlueprints'],
    }),
    getTrackedBlueprint: builder.query<BlueprintFamily, string>({
      query: (blueprintId) => `blueprint-families/${blueprintId}`,
      providesTags: (result, error, blueprintId) => [{ type: 'trackedBlueprints', id: blueprintId }],
    }),
    createBlueprintFamily: builder.mutation<BlueprintFamily, CreateBlueprintFamilyRequest>({
      query: ({ glossaryImages, allowedEventTypeIds, ...body }) => ({
        url: 'blueprint-families',
        method: 'POST',
        // Same write shape as the update: only the stored reference per screenshot entry.
        body: { ...body, ...exampleImagesBody(glossaryImages), ...acceptedEventTypesBody(allowedEventTypeIds) },
      }),
      invalidatesTags: ['trackedBlueprints'],
      onQueryStarted: (_arg, { dispatch, queryFulfilled }) => invalidateCustomerBlueprintFamilies(dispatch, queryFulfilled),
    }),
    updateBlueprintFamily: builder.mutation<
      BlueprintFamily,
      {
        blueprintId: string
        names: TranslationMap
        descriptions: TranslationMap
        discontinued: boolean
        glossaryImages?: BlueprintFamilyExampleImage[]
        allowedEventTypeIds?: string[]
      }
    >({
      query: ({ blueprintId, names, descriptions, discontinued, glossaryImages, allowedEventTypeIds }) => ({
        url: `blueprint-families/${blueprintId}`,
        method: 'PUT',
        // Only the stored fields go back; the resolved names and index are read-only
        // decoration the BE recomputes on every read. Array order is display order.
        body: { names, descriptions, discontinued, ...exampleImagesBody(glossaryImages), ...acceptedEventTypesBody(allowedEventTypeIds) },
      }),
      invalidatesTags: ['trackedBlueprints'],
      onQueryStarted: (_arg, { dispatch, queryFulfilled }) => invalidateCustomerBlueprintFamilies(dispatch, queryFulfilled),
    }),
    deleteBlueprintFamily: builder.mutation<void, string>({
      query: (blueprintId) => ({
        url: `blueprint-families/${blueprintId}`,
        method: 'DELETE',
      }),
      invalidatesTags: ['trackedBlueprints'],
      onQueryStarted: (_arg, { dispatch, queryFulfilled }) => invalidateCustomerBlueprintFamilies(dispatch, queryFulfilled),
    }),
  }),
})

export const {
  useGetTrackedBlueprintsQuery,
  useGetTrackedBlueprintQuery,
  useCreateBlueprintFamilyMutation,
  useUpdateBlueprintFamilyMutation,
  useDeleteBlueprintFamilyMutation,
} = internalMarketsApi
